import { Button } from "@svea/design-system-components-react";
import { format, getMonth, getYear } from "date-fns";
import { sv } from "date-fns/locale/sv";
import { forwardRef } from "react";
import "react-datepicker/dist/react-datepicker.css";
import ReactDatePicker, {
	type ReactDatePickerCustomHeaderProps,
	registerLocale,
} from "react-datepicker";
import { isValidDate } from "../../../utils";
import { Stack } from "../../stack";
import { SvgIcon } from "../../svg-icon";
import { Text } from "../../typography";
import { TextInput } from "../components/text-input";
import "./style.css";

registerLocale("sv", sv);

type Ref = HTMLButtonElement;
type Props = {
	onClick?: () => void;
	label?: string;
	value?: string;
};

const DateButton = forwardRef<Ref, Props>(({ onClick }, ref) => (
	<button
		className={`focus-visible:outline-interactive-primary flex h-7 w-8 items-center justify-center rounded-full leading-6 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid`}
		aria-label="Öppna datumväljare"
		type="button"
		ref={ref}
		onClick={onClick}
	>
		<SvgIcon imageSrc="CALENDAR" className="text-interactive-primary" />
	</button>
));

const DatePickerHeader = ({
	changeMonth,
	changeYear,
	monthDate,
}: ReactDatePickerCustomHeaderProps) => {
	const handlePrevMonth = (month: number) => {
		if (month === 0) {
			changeYear(getYear(monthDate) - 1);
			changeMonth(11);
		} else {
			changeMonth(getMonth(monthDate) - 1);
		}
	};

	const handleNextMonth = (month: number) => {
		if (month === 11) {
			changeYear(getYear(monthDate) + 1);
			changeMonth(0);
		} else {
			changeMonth(getMonth(monthDate) + 1);
		}
	};

	return (
		<div className="px-2">
			<Stack gap={2} justify="between">
				<Stack direction="row" justify="between">
					<Stack direction="row" gap={2}>
						<Button
							variant="icon"
							icon="chevronLeft"
							aria-label="Föregående år"
							size="sm"
							onPress={() => changeYear(getYear(monthDate) - 1)}
						/>
						<Text bold className="text-sm">
							{getYear(monthDate)}
						</Text>
						<Button
							variant="icon"
							icon="chevronRight"
							aria-label="Nästa år"
							size="sm"
							onPress={() => changeYear(getYear(monthDate) + 1)}
						/>
					</Stack>
					<Stack direction="row" gap={2} justify="between">
						<Button
							variant="icon"
							icon="chevronLeft"
							aria-label="Föregående Månad"
							size="sm"
							onPress={() => handlePrevMonth(getMonth(monthDate))}
						/>
						<Text bold className="min-w-16 text-sm capitalize">
							{format(monthDate, "MMMM", { locale: sv })}
						</Text>
						<Button
							variant="icon"
							icon="chevronRight"
							aria-label="Nästa Månad"
							size="sm"
							onPress={() => handleNextMonth(getMonth(monthDate))}
						/>
					</Stack>
				</Stack>
			</Stack>
		</div>
	);
};

export type ReactDatePickerStandaloneProps = React.ComponentProps<typeof ReactDatePicker>;

export const DatePickerStandalone = forwardRef<
	React.ComponentRef<typeof DatePicker>,
	ReactDatePickerStandaloneProps
>((props, ref) => {
	return (
		<ReactDatePicker
			ref={ref}
			{...props}
			renderCustomHeader={DatePickerHeader}
			locale="sv"
			dateFormat="yyyy-MM-dd"
		/>
	);
});

export type IDatepPicker = {
	name: string;
	label: string;
	onChange: (d: string | null) => void;
	value?: string;
	autoFocus?: boolean;
	minDate?: Date;
	maxDate?: Date;
	excludeDates?: Date[];
	open?: boolean;
	error?: boolean;
};

export const DatePicker = ({
	name,
	label,
	onChange,
	value,
	autoFocus,
	minDate,
	maxDate,
	excludeDates,
	open,
	error,
}: IDatepPicker) => {
	return (
		<TextInput
			fullWidth
			name={name}
			label={label}
			onChange={(e) => {
				onChange(e.target.value);
			}}
			value={value || ""}
			placeholder="YYYY-MM-DD"
			autoComplete="off"
			autoFocus={autoFocus}
			error={error}
			endAdornment={
				<DatePickerStandalone
					customInput={<DateButton label={label} />}
					fixedHeight
					onChange={(date: Date | null) => date && onChange(format(date, "yyyy-MM-dd"))}
					minDate={minDate}
					maxDate={maxDate}
					open={open}
					popperProps={{
						placement: "bottom-end",
					}}
					popperModifiers={[
						{
							name: "placement",
							fn: (state) => {
								const { y, x } = state;

								return {
									...state,
									y: y + 8,
									x: x + 16,
								};
							},
						},
					]}
					excludeDates={excludeDates}
					selected={value && isValidDate(value) ? new Date(value) : undefined}
				/>
			}
		/>
	);
};
